import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import type { GenericOption } from 'pages/AutomationEngine/types';
import { VariableDataType } from 'ts-components/inputs/ValueInput/types';
import { isUUIDValid } from 'ts-components/inputs/ValueInput';
import type { ArraySeparator } from '../../types';
import { ARRAY_SEPARATORS } from '../../constants';
import { StyledLongText } from './styles';

export const ArrayOfStringsInput: FC<{
  value?: string[];
  onChange: (value: string[], isValid?: boolean) => void;
  dataType: Extract<VariableDataType, 'string' | 'uuid'>;
  error?: string[];
  setError: (prop: 'value', error: string[]) => void;
  validate?: any;
  disabled?: boolean;
  arraySeparator: ArraySeparator;
  setArraySeparator: (
    separator: ArraySeparator,
    value?: string[],
    isValid?: boolean
  ) => void;
}> = ({
  value,
  onChange,
  dataType,
  error,
  setError,
  validate,
  disabled,
  arraySeparator = ARRAY_SEPARATORS.comma,
  setArraySeparator,
}) => {
  const { t } = useTranslation();

  const seperatorTypeOptions = useMemo(
    () => [
      { label: t('Comma-Delimited'), value: ARRAY_SEPARATORS.comma },
      { label: t('New Line'), value: ARRAY_SEPARATORS.newline },
    ],
    [t]
  );

  const [parsedValue, setParsedValue] = useState(() => {
    const splitValue = arraySeparator === ARRAY_SEPARATORS.comma ? ',' : '\n';
    return (value || []).join(splitValue);
  });

  const parseValues = useCallback(
    (stringValue: string, splitValue: ',' | '\n') => {
      let isValid = true;
      const parsedValues = stringValue.trim()
        ? stringValue.split(splitValue)
        : [];
      if (dataType === 'uuid') {
        parsedValues.forEach((value) => {
          const trimmedValue = value.trim();
          if (isValid && trimmedValue && !isUUIDValid(trimmedValue)) {
            setError('value', [
              t(
                'A UUID value is expected in the format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX (dashes optional)'
              ),
            ]);
            isValid = false;
          }
        });
      }

      return { parsedValues, isValid };
    },
    [dataType, setError, t]
  );

  const handleChange = useCallback(() => {
    const { parsedValues, isValid } = parseValues(
      parsedValue,
      arraySeparator === ARRAY_SEPARATORS.comma ? ',' : '\n'
    );
    onChange(parsedValues, isValid);
  }, [onChange, parseValues, parsedValue, arraySeparator]);

  const handleArraySeparatorChange = useCallback(
    ({ value }: GenericOption<ArraySeparator>) => {
      const { parsedValues, isValid } = parseValues(
        parsedValue,
        value === ARRAY_SEPARATORS.comma ? ',' : '\n'
      );
      setArraySeparator(value, parsedValues, isValid);
    },
    [setArraySeparator, parseValues, parsedValue]
  );

  return (
    <>
      <Select
        fullWidth
        label={t('Choose Value Separator')}
        options={seperatorTypeOptions}
        value={arraySeparator}
        onChange={handleArraySeparatorChange}
        margin
        disabled={disabled}
      />
      <StyledLongText
        label={t('Enter Value in Chosen Format')}
        data-qa="static-value"
        enableResize={false}
        value={parsedValue}
        onChange={setParsedValue}
        onBlur={handleChange}
        placeholder={t('Place Values Here')}
        fullHeight
        error={error}
        validate={validate}
        disabled={disabled}
      />
    </>
  );
};
